'use client'

import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/styles/cn'

interface IFadeComponentProps {
    className?: string
}

interface IUseVerticalScrollFadeOptions {
    topThreshold?: number
    bottomBuffer?: number
}

interface IUseVerticalScrollFadeReturn<T extends HTMLElement> {
    scrollRef: React.RefObject<T | null>
    updateScrollState: () => void
    hasScrolledDown: boolean
    canScrollDown: boolean
    TopFade: React.FC<IFadeComponentProps>
    BottomFade: React.FC<IFadeComponentProps>
}

export function useVerticalScrollFade<T extends HTMLElement = HTMLDivElement>(
    options: IUseVerticalScrollFadeOptions = {}
): IUseVerticalScrollFadeReturn<T> {
    const { topThreshold = 10, bottomBuffer = 5 } = options

    const scrollRef = useRef<T | null>(null)
    const [hasScrolledDown, setHasScrolledDown] = useState(false)
    const [canScrollDown, setCanScrollDown] = useState(false)

    const updateScrollState = useCallback(() => {
        if (scrollRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
            setHasScrolledDown(scrollTop > topThreshold)
            setCanScrollDown(scrollTop + clientHeight < scrollHeight - bottomBuffer)
        }
    }, [topThreshold, bottomBuffer])

    useEffect(() => {
        updateScrollState()
    }, [updateScrollState])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        const observer = new ResizeObserver(updateScrollState)
        observer.observe(el)
        for (const child of el.children) observer.observe(child)
        const mo = new MutationObserver(() => {
            observer.disconnect()
            observer.observe(el)
            for (const child of el.children) observer.observe(child)
            updateScrollState()
        })
        mo.observe(el, { childList: true })
        return () => { observer.disconnect(); mo.disconnect() }
    }, [updateScrollState])

    const TopFade = useMemo(() => {
        const Component: React.FC<IFadeComponentProps> = ({ className }) => (
            <motion.div
                animate={{ opacity: hasScrolledDown ? 1 : 0 }}
                transition={{ duration: 0.15 }}
                className={cn(
                    'absolute top-0 left-0 right-0 h-4 bg-linear-to-b to-transparent z-10 pointer-events-none',
                    className
                )}
            />
        )
        Component.displayName = 'TopFade'
        return Component
    }, [hasScrolledDown])

    const BottomFade = useMemo(() => {
        const Component: React.FC<IFadeComponentProps> = ({ className }) => (
            <motion.div
                animate={{ opacity: canScrollDown ? 1 : 0 }}
                transition={{ duration: 0.15 }}
                className={cn(
                    'absolute bottom-0 left-0 right-0 h-4 bg-linear-to-t to-transparent z-10 pointer-events-none',
                    className
                )}
            />
        )
        Component.displayName = 'BottomFade'
        return Component
    }, [canScrollDown])

    return {
        scrollRef,
        updateScrollState,
        hasScrolledDown,
        canScrollDown,
        TopFade,
        BottomFade,
    }
}
